import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import posthog from '../utils/posthog-stub'; // FERPA: Using no-op stub until self-hosted PostHog is configured
import './GuestTutorExperience.css';

/**
 * GuestTutorExperience - Top-Tier 2025 Landing Page
 *
 * Design inspiration: Linear, Vercel, Notion, Arc, Apple
 * Features:
 * - Animated gradient hero with typing effect
 * - Bento grid layout
 * - Scroll-triggered animations
 * - Dark mode support
 * - Refined micro-interactions
 * - SVG icons (no emojis) for professional aesthetic
 */

// SVG Icon Components - Clean, professional design
const Icons = {
  Mic: () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/>
      <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
      <line x1="12" x2="12" y1="19" y2="22"/>
    </svg>
  ),
  Screen: () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect width="20" height="14" x="2" y="3" rx="2"/>
      <line x1="8" x2="16" y1="21" y2="21"/>
      <line x1="12" x2="12" y1="17" y2="21"/>
    </svg>
  ),
  Users: () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
      <circle cx="9" cy="7" r="4"/>
      <path d="M22 21v-2a4 4 0 0 0-3-3.87"/>
      <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  ),
  Book: () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20"/>
    </svg>
  ),
  Brain: () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 5a3 3 0 1 0-5.997.125 4 4 0 0 0-2.526 5.77 4 4 0 0 0 .556 6.588A4 4 0 1 0 12 18Z"/>
      <path d="M12 5a3 3 0 1 1 5.997.125 4 4 0 0 1 2.526 5.77 4 4 0 0 1-.556 6.588A4 4 0 1 1 12 18Z"/>
      <path d="M15 13a4.5 4.5 0 0 1-3-4 4.5 4.5 0 0 1-3 4"/>
      <path d="M12 18v4"/>
    </svg>
  ),
  Shield: () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/>
      <path d="m9 12 2 2 4-4"/>
    </svg>
  ),
  Sun: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="4"/>
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41"/>
    </svg>
  ),
  Moon: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z"/>
    </svg>
  ),
  Check: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  ),
  Star: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
    </svg>
  ),
  Arrow: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12h14M12 5l7 7-7 7"/>
    </svg>
  ),
};

// Typing effect hook
const useTypingEffect = (words, typingSpeed = 100, deletingSpeed = 50, pauseDuration = 2000) => {
  const [displayText, setDisplayText] = useState('');
  const [wordIndex, setWordIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const currentWord = words[wordIndex];

    const timeout = setTimeout(() => {
      if (!isDeleting) {
        if (displayText.length < currentWord.length) {
          setDisplayText(currentWord.slice(0, displayText.length + 1));
        } else {
          setTimeout(() => setIsDeleting(true), pauseDuration);
        }
      } else {
        if (displayText.length > 0) {
          setDisplayText(displayText.slice(0, -1));
        } else {
          setIsDeleting(false);
          setWordIndex((prev) => (prev + 1) % words.length);
        }
      }
    }, isDeleting ? deletingSpeed : typingSpeed);

    return () => clearTimeout(timeout);
  }, [displayText, isDeleting, wordIndex, words, typingSpeed, deletingSpeed, pauseDuration]);

  return displayText;
};

// Scroll-triggered section component
const ScrollSection = ({ children, className, delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.section
      ref={ref}
      className={className}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
      transition={{ duration: 0.6, delay, ease: [0.25, 0.1, 0.25, 1] }}
    >
      {children}
    </motion.section>
  );
};

// Bento card with hover effect
const BentoCard = ({ icon: Icon, title, description, size = 'normal', gradient }) => {
  return (
    <motion.div
      className={`bento-card bento-${size}`}
      whileHover={{ y: -4, scale: 1.01 }}
      transition={{ duration: 0.2 }}
      style={gradient ? { background: gradient } : {}}
    >
      <span className="bento-icon">{Icon && <Icon />}</span>
      <h3 className="bento-title">{title}</h3>
      <p className="bento-description">{description}</p>
    </motion.div>
  );
};

// Star rating component
const StarRating = ({ count = 5 }) => (
  <div className="testimonial-stars">
    {[...Array(count)].map((_, i) => (
      <Icons.Star key={i} />
    ))}
  </div>
);

const GuestTutorExperience = () => {
  const navigate = useNavigate();
  const [code, setCode] = useState(['', '', '', '']);
  const [isJoining, setIsJoining] = useState(false);
  const [joinError, setJoinError] = useState('');
  const [isDarkMode, setIsDarkMode] = useState(false);
  // Teachers arriving from /teachers ("try Mira first") get a persistent
  // conversion ribbon so the free-classroom offer is one tap away at the
  // moment the session lands — aha first, form after.
  const [showTeacherRibbon, setShowTeacherRibbon] = useState(
    () => (new URLSearchParams(window.location.search).get('source') || '').startsWith('teacher')
  );
  const inputRefs = [useRef(null), useRef(null), useRef(null), useRef(null)];
  const heroRef = useRef(null);

  // Typing effect for hero
  const typingText = useTypingEffect([
    'math homework',
    'science projects',
    'essay writing',
    'test prep',
    'any subject'
  ], 80, 40, 1500);

  // Parallax scroll effect
  const { scrollYProgress } = useScroll();
  const heroY = useTransform(scrollYProgress, [0, 0.3], [0, -50]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.2], [1, 0.8]);

  // Check system dark mode preference
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    setIsDarkMode(mediaQuery.matches);

    const handler = (e) => setIsDarkMode(e.matches);
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  // Apply dark mode class
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  // Initialize anonymous tracking
  useEffect(() => {
    let anonId = localStorage.getItem('morpheus_anon_id');
    if (!anonId) {
      anonId = `anon-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
      localStorage.setItem('morpheus_anon_id', anonId);
      try { posthog.capture('first_time_visitor'); } catch (e) {}
    }
    try { posthog.identify(anonId); } catch (e) {}
  }, []);

  // Navigation helper
  const navigateToApp = (path) => {
    const isLocalDev = window.location.hostname === 'localhost' ||
                       window.location.hostname === '127.0.0.1';
    if (isLocalDev) {
      navigate(path);
    } else {
      let baseDomain = window.location.hostname.replace('www.', '').replace('app.', '');
      window.location.href = `https://app.${baseDomain}${path}`;
    }
  };

  const handleStartSession = () => {
    try { posthog.capture('start_session_clicked'); } catch (e) {}
    navigateToApp('/guest-tutor');
  };

  // Code input handlers
  const handleDigitChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newCode = [...code];
    newCode[index] = value.slice(-1);
    setCode(newCode);
    setJoinError('');

    if (value && index < 3) {
      inputRefs[index + 1].current?.focus();
    }
    if (value && index === 3) {
      const fullCode = newCode.join('');
      if (fullCode.length === 4) joinWithCode(fullCode);
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs[index - 1].current?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 4);
    if (pastedData.length === 4) {
      setCode(pastedData.split(''));
      joinWithCode(pastedData);
    }
  };

  const joinWithCode = async (fullCode) => {
    setIsJoining(true);
    setJoinError('');
    try { posthog.capture('join_code_submitted'); } catch (e) {}

    try {
      const response = await fetch('/api/join-study-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: fullCode })
      });
      const data = await response.json();

      if (response.ok && data.success) {
        window.location.href = `/join/${data.session_token}`;
      } else {
        setJoinError(data.error || 'Invalid code');
        setCode(['', '', '', '']);
        inputRefs[0].current?.focus();
      }
    } catch (error) {
      setJoinError('Connection error');
      setCode(['', '', '', '']);
    } finally {
      setIsJoining(false);
    }
  };

  return (
    <div className="landing-2025" data-theme={isDarkMode ? 'dark' : 'light'}>
      {/* Ambient gradient background */}
      <div className="ambient-gradient" />

      {/* Navigation */}
      <header className="nav-header">
        <div className="nav-content">
          <div className="nav-logo">
            <img src="/images/TAPPY-logo-black.png" alt="Tappy" className="logo-light" />
            <img src="/images/TAPPY-logo-white.png" alt="Tappy" className="logo-dark" />
          </div>

          <nav className="nav-links">
            {/* Audience lanes — teachers get the free-tier pitch (Flask-served
                static page, so a real anchor, not client-side navigation) */}
            <a href="/teachers" className="nav-link nav-link-teachers">Teachers</a>
            <button onClick={() => navigateToApp('/for-parents')} className="nav-link">Parents</button>
            <button onClick={() => navigateToApp('/pricing')} className="nav-link">Pricing</button>
            <button
              onClick={() => setIsDarkMode(!isDarkMode)}
              className="nav-link theme-toggle"
              aria-label="Toggle theme"
            >
              {isDarkMode ? <Icons.Sun /> : <Icons.Moon />}
            </button>
            <button onClick={() => navigateToApp('/login')} className="nav-link">Log in</button>
            <button onClick={() => navigateToApp('/login?mode=signup')} className="nav-btn-primary">
              Get Started
            </button>
          </nav>
        </div>

        {/* Scroll progress indicator */}
        <motion.div
          className="scroll-progress"
          style={{ scaleX: scrollYProgress }}
        />
      </header>

      {/* Teacher conversion ribbon — shown when arriving via /teachers */}
      {showTeacherRibbon && (
        <div className="teacher-ribbon" role="complementary">
          <span className="teacher-ribbon-text">
            Like what you hear? Give this to your whole class — free forever.
          </span>
          <a href="/login?role=teacher" className="teacher-ribbon-cta">
            Get your free classroom →
          </a>
          <button
            className="teacher-ribbon-dismiss"
            aria-label="Dismiss"
            onClick={() => setShowTeacherRibbon(false)}
          >
            ✕
          </button>
        </div>
      )}

      {/* Hero Section */}
      <motion.section
        ref={heroRef}
        className="hero-2025"
        style={{ y: heroY, opacity: heroOpacity }}
      >
        <div className="hero-content">
          {/* Floating badge */}
          <motion.div
            className="hero-badge"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <span className="badge-dot" />
            <span>AI-powered learning</span>
          </motion.div>

          {/* Main headline with gradient */}
          <motion.h1
            className="hero-headline"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
          >
            Get help with
            <br />
            <span className="gradient-text">{typingText}</span>
            <span className="typing-cursor">|</span>
          </motion.h1>

          <motion.p
            className="hero-subheadline"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            Talk to Tappy. Just speak naturally and get instant help with any homework question.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            className="hero-ctas"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            <motion.button
              className="cta-primary"
              onClick={handleStartSession}
              whileHover={{ scale: 1.02, boxShadow: '0 20px 40px rgba(41, 122, 146, 0.3)' }}
              whileTap={{ scale: 0.98 }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z" fill="currentColor"/>
                <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" fill="currentColor"/>
              </svg>
              Start talking — it's free
            </motion.button>

            <button className="cta-secondary" onClick={() => navigateToApp('/pricing')}>
              View pricing
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
          </motion.div>

          {/* Join friend code */}
          <motion.div
            className="hero-join-code"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
          >
            <span className="join-label">Have a friend's code?</span>
            <div className="code-inputs">
              {code.map((digit, index) => (
                <input
                  key={index}
                  ref={inputRefs[index]}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleDigitChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={index === 0 ? handlePaste : undefined}
                  disabled={isJoining}
                  className={`code-box ${joinError ? 'error' : ''}`}
                />
              ))}
            </div>
            {joinError && <span className="join-error">{joinError}</span>}
          </motion.div>
        </div>

        {/* Hero visual - abstract shapes */}
        <div className="hero-visual">
          <div className="floating-shape shape-1" />
          <div className="floating-shape shape-2" />
          <div className="floating-shape shape-3" />
        </div>
      </motion.section>

      {/* Trust signals */}
      <ScrollSection className="trust-section">
        <p className="trust-label">Trusted by students and parents nationwide</p>
        <div className="trust-metrics">
          <div className="metric">
            <span className="metric-value">10M+</span>
            <span className="metric-label">Questions answered</span>
          </div>
          <div className="metric-divider" />
          <div className="metric">
            <span className="metric-value">4.9★</span>
            <span className="metric-label">Parent rating</span>
          </div>
          <div className="metric-divider" />
          <div className="metric">
            <span className="metric-value">K-12</span>
            <span className="metric-label">All grade levels</span>
          </div>
        </div>
      </ScrollSection>

      {/* Bento Features Grid */}
      <ScrollSection className="bento-section">
        <div className="section-header">
          <h2 className="section-title">Everything you need to succeed</h2>
          <p className="section-subtitle">Powerful AI tutoring designed for how students actually learn</p>
        </div>

        <div className="bento-grid">
          <BentoCard
            size="large"
            icon={Icons.Mic}
            title="Just talk"
            description="No typing needed. Ask questions naturally like you're talking to a tutor. Tappy understands context and follows your train of thought."
            gradient="linear-gradient(135deg, rgba(41, 122, 146, 0.08), rgba(41, 122, 146, 0.02))"
          />
          <BentoCard
            size="normal"
            icon={Icons.Screen}
            title="Show your work"
            description="Share your screen so Tappy can see exactly what you're working on."
          />
          <BentoCard
            size="normal"
            icon={Icons.Users}
            title="Study together"
            description="Invite friends to join your session. Learn collaboratively with AI guidance."
          />
          <BentoCard
            size="wide"
            icon={Icons.Book}
            title="Every subject, every grade"
            description="Math, science, English, history, languages, coding — Tappy helps with all of it. From elementary through high school."
          />
          <BentoCard
            size="normal"
            icon={Icons.Brain}
            title="Learns your style"
            description="Tappy adapts to how you learn best — visual, step-by-step, or conceptual."
          />
          <BentoCard
            size="normal"
            icon={Icons.Shield}
            title="Safe & private"
            description="COPPA & FERPA compliant. Your data stays private and secure."
          />
        </div>
      </ScrollSection>

      {/* How it works */}
      <ScrollSection className="how-section">
        <div className="section-header">
          <h2 className="section-title">Start learning in seconds</h2>
          <p className="section-subtitle">No downloads, no accounts required to try</p>
        </div>

        <div className="steps-grid">
          <motion.div
            className="step-card"
            whileHover={{ y: -4 }}
          >
            <div className="step-number">1</div>
            <h3>Click to start</h3>
            <p>Hit the button and allow microphone access</p>
          </motion.div>
          <div className="step-arrow"><Icons.Arrow /></div>
          <motion.div
            className="step-card"
            whileHover={{ y: -4 }}
          >
            <div className="step-number">2</div>
            <h3>Ask anything</h3>
            <p>Speak your question naturally, just like texting a friend</p>
          </motion.div>
          <div className="step-arrow"><Icons.Arrow /></div>
          <motion.div
            className="step-card"
            whileHover={{ y: -4 }}
          >
            <div className="step-number">3</div>
            <h3>Get help</h3>
            <p>Tappy explains, guides, and helps you understand</p>
          </motion.div>
        </div>
      </ScrollSection>

      {/* Teacher wedge — the Gemini Notebook outcome loop. The homepage
          stays student-first; this band routes teacher visitors to the
          accessory pitch ("phone case for your Gemini Notebook"). */}
      <ScrollSection className="teacher-band-section">
        <div className="teacher-band">
          <div className="teacher-band-copy">
            <div className="teacher-band-kicker">For teachers · Free forever</div>
            <h2>Assign Gemini Notebook materials? Now they report back.</h2>
            <p>
              Keep your workflow — Tappy adds the missing piece: students talk your
              materials through with Mira by voice, and you see mastery,
              misconceptions, and time-on-task for every student by morning.
              Something NotebookLM can't show you.
            </p>
            <a href="/teachers" className="teacher-band-cta">
              See how it works →
            </a>
          </div>
          <div className="teacher-band-stats">
            <div className="tb-stat"><strong>1 link</strong><span>students join with a first name only</span></div>
            <div className="tb-stat"><strong>30 students</strong><span>free forever — not a trial</span></div>
            <div className="tb-stat"><strong>0 new habits</strong><span>post it to Google Classroom like always</span></div>
          </div>
        </div>
      </ScrollSection>

      {/* Pricing */}
      <ScrollSection className="pricing-section-2025">
        <div className="section-header">
          <h2 className="section-title">Simple, transparent pricing</h2>
          <p className="section-subtitle">Start free, upgrade when you're ready</p>
        </div>

        <div className="pricing-cards">
          <motion.div
            className="price-card"
            whileHover={{ y: -4 }}
          >
            <h3>Free</h3>
            <div className="price">$0</div>
            <p className="price-desc">Try it out</p>
            <ul>
              <li>Voice tutoring session</li>
              <li>All subjects</li>
              <li>Basic features</li>
            </ul>
            <button className="price-btn secondary" onClick={handleStartSession}>
              Try free
            </button>
          </motion.div>

          <motion.div
            className="price-card featured"
            whileHover={{ y: -4 }}
          >
            <div className="featured-badge">Most popular</div>
            <h3>Unlimited</h3>
            <div className="price">$9.99<span>/mo</span></div>
            <p className="price-desc">Everything you need</p>
            <ul>
              <li>Unlimited voice tutoring</li>
              <li>Screen sharing</li>
              <li>Study with friends</li>
              <li>Progress tracking</li>
              <li>Priority support</li>
            </ul>
            <button className="price-btn primary" onClick={() => navigateToApp('/login?mode=signup&tier=unlimited')}>
              Get started
            </button>
          </motion.div>

          <motion.div
            className="price-card"
            whileHover={{ y: -4 }}
          >
            <h3>Family</h3>
            <div className="price">$19.99<span>/mo</span></div>
            <p className="price-desc">Up to 5 students</p>
            <ul>
              <li>Everything in Unlimited</li>
              <li>5 student accounts</li>
              <li>Parent dashboard</li>
              <li>Family leaderboards</li>
            </ul>
            <button className="price-btn secondary" onClick={() => navigateToApp('/login?mode=signup&tier=home')}>
              Get started
            </button>
          </motion.div>
        </div>

        <div className="b2b-link">
          <p>Are you a school or district? <a href="mailto:schools@teachtappy.com">Talk to our team →</a></p>
        </div>
      </ScrollSection>

      {/* Testimonials */}
      <ScrollSection className="testimonials-2025">
        <div className="section-header">
          <h2 className="section-title">Loved by families</h2>
        </div>

        <div className="testimonial-grid">
          <motion.div className="testimonial" whileHover={{ y: -2 }}>
            <StarRating count={5} />
            <p>"My daughter went from dreading math homework to actually enjoying it. The voice interaction makes it feel like having a real tutor."</p>
            <div className="testimonial-author">
              <div className="author-avatar">MS</div>
              <div>
                <strong>Maria S.</strong>
                <span>Parent of 7th grader</span>
              </div>
            </div>
          </motion.div>

          <motion.div className="testimonial" whileHover={{ y: -2 }}>
            <StarRating count={5} />
            <p>"Finally, something that helps without just giving the answer. My son actually understands the concepts now."</p>
            <div className="testimonial-author">
              <div className="author-avatar">JT</div>
              <div>
                <strong>James T.</strong>
                <span>Parent of 9th grader</span>
              </div>
            </div>
          </motion.div>

          <motion.div className="testimonial" whileHover={{ y: -2 }}>
            <StarRating count={5} />
            <p>"The study-with-friends feature is genius. My kids actually want to do homework together now."</p>
            <div className="testimonial-author">
              <div className="author-avatar">LK</div>
              <div>
                <strong>Lisa K.</strong>
                <span>Parent of 5th & 8th graders</span>
              </div>
            </div>
          </motion.div>
        </div>
      </ScrollSection>

      {/* Final CTA */}
      <ScrollSection className="final-cta">
        <div className="cta-card">
          <h2>Ready to make homework easier?</h2>
          <p>Join thousands of students getting help with Tappy</p>
          <motion.button
            className="cta-primary large"
            onClick={handleStartSession}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Start talking — it's free
          </motion.button>
        </div>
      </ScrollSection>

      {/* Footer */}
      <footer className="footer-2025">
        <div className="footer-content">
          <div className="footer-brand">
            <img src="/images/TAPPY-logo-black.png" alt="Tappy" className="logo-light" />
            <img src="/images/TAPPY-logo-white.png" alt="Tappy" className="logo-dark" />
            <p>AI tutoring for the next generation</p>
          </div>

          <div className="footer-links">
            <div className="footer-col">
              <h4>Product</h4>
              <button onClick={() => navigateToApp('/pricing')}>Pricing</button>
              <button onClick={() => navigateToApp('/login')}>Log in</button>
              <button onClick={() => navigateToApp('/login?mode=signup')}>Sign up</button>
            </div>
            <div className="footer-col">
              <h4>Legal</h4>
              <a href="/terms-of-service.html" target="_blank" rel="noopener noreferrer">Terms</a>
              <a href="/privacy-policy.html" target="_blank" rel="noopener noreferrer">Privacy</a>
            </div>
            <div className="footer-col">
              <h4>Support</h4>
              <a href="mailto:care@teachtappy.com">Contact</a>
              <button onClick={() => navigateToApp('/sponsor')}>Sponsor a Student</button>
            </div>
            <div className="footer-col">
              <h4>Company</h4>
              <a href="/research">Research</a>
            </div>
          </div>
        </div>

        <div className="footer-bottom">
          <p>© 2026 Teach Tappy. Powered by Morpheus RL.</p>
        </div>
      </footer>
    </div>
  );
};

export default GuestTutorExperience;
